import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    IconButton,
    Typography,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import {
    Save as SaveIcon,
    ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import {
    productConversionService,
    CreateConversionRequest,
    CostAllocationMethod,
} from "../../services/productConversionService";
import { productService } from "../../services/productService";
import { Product } from "../../types";
import {
    ProductConversionForm,
    ConversionFormItem,
    hasDuplicateProductsInRole,
    hasOverlapBetweenRoles,
} from "../../components/conversions/ProductConversionForm";

const CreateConversion: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const backTo =
        (location.state as { from?: string } | null)?.from ||
        "/inventory/conversions";

    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<Product[]>([]);
    const [items, setItems] = useState<ConversionFormItem[]>([]);
    const [costAllocationMethod, setCostAllocationMethod] =
        useState<CostAllocationMethod>("SALES_VALUE");
    const [additionalCost, setAdditionalCost] = useState(0);
    const [notes, setNotes] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({ open: false, message: "", severity: "success" });

    useEffect(() => {
        productService
            .getProducts({ active_only: true, limit: 1000 })
            .then((res) => setProducts(res.data || []))
            .catch(() => showSnackbar("Failed to load products", "error"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showSnackbar = (message: string, severity: "success" | "error") => {
        setSnackbar({ open: true, message, severity });
    };

    const inputs = items.filter((i) => i.role === "INPUT");
    const outputs = items.filter((i) => i.role === "OUTPUT");

    const validate = (): string | null => {
        if (inputs.length === 0) return "Add at least one input product";
        if (outputs.length === 0) return "Add at least one output product";
        if (items.some((i) => !i.product_id || i.quantity <= 0))
            return "Every line needs a product and a quantity greater than zero";
        if (hasDuplicateProductsInRole(items, "INPUT"))
            return "The same product appears on multiple input lines — combine them into one line";
        if (hasDuplicateProductsInRole(items, "OUTPUT"))
            return "The same product appears on multiple output lines — combine them into one line";
        if (hasOverlapBetweenRoles(items))
            return "A product can't be both an input and an output in the same conversion";
        if (outputs.length > 1 && costAllocationMethod === "MANUAL") {
            const totalPct = outputs.reduce(
                (sum, o) => sum + (Number(o.allocation_percentage) || 0),
                0,
            );
            if (Math.abs(totalPct - 100) > 0.5)
                return `Manual allocation percentages must sum to 100 (currently ${totalPct.toFixed(2)})`;
        }
        return null;
    };

    const handleSubmitClick = () => {
        const error = validate();
        if (error) {
            showSnackbar(error, "error");
            return;
        }
        setConfirmOpen(true);
    };

    const handleConfirm = async () => {
        setConfirmOpen(false);
        try {
            setLoading(true);
            const request: CreateConversionRequest = {
                items: items.map((i) => ({
                    product_id: i.product_id,
                    role: i.role,
                    quantity: i.quantity,
                    allocation_percentage:
                        i.role === "OUTPUT" && costAllocationMethod === "MANUAL"
                            ? i.allocation_percentage
                            : undefined,
                })),
                cost_allocation_method:
                    outputs.length > 1 ? costAllocationMethod : undefined,
                additional_cost: additionalCost || 0,
                notes: notes || undefined,
            };
            await productConversionService.createConversion(request);
            showSnackbar("Conversion created successfully", "success");
            setTimeout(() => navigate(backTo), 1200);
        } catch (error: any) {
            const detail = error?.response?.data?.detail;
            showSnackbar(
                typeof detail === "string" ? detail : "Failed to create conversion",
                "error",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 0, sm: 3 } }}>
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={() => navigate(backTo)} sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                >
                    New Product Conversion
                </Typography>
            </Box>

            <ProductConversionForm
                products={products}
                items={items}
                setItems={setItems}
                costAllocationMethod={costAllocationMethod}
                setCostAllocationMethod={setCostAllocationMethod}
                additionalCost={additionalCost}
                setAdditionalCost={setAdditionalCost}
                notes={notes}
                setNotes={setNotes}
            />

            <Box display="flex" justifyContent="space-between" mt={3}>
                <Button variant="outlined" onClick={() => navigate(backTo)}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSubmitClick}
                    disabled={loading || inputs.length === 0 || outputs.length === 0}
                >
                    {loading ? "Creating..." : "Create Conversion"}
                </Button>
            </Box>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Complete this conversion?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will immediately consume the input stock, produce the
                        output stock, and update product costs. Conversions can't be
                        undone from here — a mistake has to be corrected with a manual
                        stock/cost adjustment afterward.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirm} variant="contained" color="primary">
                        Yes, complete conversion
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CreateConversion;
